import React, { useEffect, useState, useRef, useMemo } from "react";
import { MapContainer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./CitizenImpactMap.css";
import L from "leaflet";

/* ================= HELPERS ================= */

const normalize = (v) => {
  if (!v) return "";
  return String(v).trim().toLowerCase().replace(/\s+/g, " ");
};

const CitizenImpactMap = ({
  selectedGender,
  selectedAgeRange,
  selectedState,
  selectedDistrict,
  selectedProjectName,
  selectedDateRange
}) => {

  const mapRef = useRef(null);

  const [stateGeo, setStateGeo] = useState(null);
  const [districtGeo, setDistrictGeo] = useState(null);

  const [stateData, setStateData] = useState([]);
  const [districtData, setDistrictData] = useState([]);

  const [showDistrictMap, setShowDistrictMap] = useState(false);
  const [clickedState, setClickedState] = useState(null);

  /* ================= LOAD STATE GEO ================= */

  useEffect(() => {
    fetch("/india.json")
      .then(res => res.json())
      .then(data => {
        setStateGeo(data);
        if (mapRef.current) {
          mapRef.current.fitBounds(L.geoJSON(data).getBounds());
        }
      });
  }, []);

  /* ================= FETCH STATE DATA ================= */

  useEffect(() => {

    // If already on district map → do NOT reset
    if (showDistrictMap && clickedState) {
      loadDistricts(clickedState);
      return;
    }

    fetch("https://api-sales-insights.haqdarshak.com/api/citizen-impact/map-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        gender: selectedGender || [],
        ageRange: selectedAgeRange || [],
        state: selectedState || [],
        district: selectedDistrict || [],
        projectName: selectedProjectName || [],
        startDate: selectedDateRange?.start,
        endDate: selectedDateRange?.end
      })
    })
      .then(res => res.json())
      .then(data => {
        setStateData(Array.isArray(data) ? data : []);
      })
      .catch(() => setStateData([]));

  }, [
    selectedGender,
    selectedAgeRange,
    selectedState,
    selectedDistrict,
    selectedProjectName,
    selectedDateRange,
    showDistrictMap   
  ]);

  /* ================= LOAD DISTRICTS ================= */

  const loadDistricts = (stateName) => {

    setClickedState(stateName);
    setShowDistrictMap(true);

    fetch("https://api-sales-insights.haqdarshak.com/api/citizen-impact/district-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        gender: selectedGender || [],
        ageRange: selectedAgeRange || [],
        state: [stateName],
        district: selectedDistrict || [],
        projectName: selectedProjectName || [],
        startDate: selectedDateRange?.start,
        endDate: selectedDateRange?.end
      })
    })
      .then(res => res.json())
      .then(data => {
        setDistrictData(Array.isArray(data) ? data : []);
      })
      .catch(() => setDistrictData([]));

    fetch("/india_district.geojson")
      .then(res => res.json())
      .then(geo => {

        const filtered = geo.features.filter(
          f => normalize(f.properties.st_nm) === normalize(stateName)
        );

        setDistrictGeo({
          type: "FeatureCollection",
          features: filtered
        });

        if (mapRef.current && filtered.length) {
          mapRef.current.fitBounds(L.geoJSON(filtered).getBounds());
        }
      });
  };

  /* ================= AUTO DISTRICT LOAD ================= */

  useEffect(() => {
    if (selectedDistrict?.length > 0 && selectedState?.length > 0) {
      loadDistricts(selectedState[0]);
    }
  }, [selectedDistrict]);

  /* ================= LOOKUP MAPS ================= */

  const stateMap = useMemo(() => {
    const obj = {};
    stateData.forEach(s => {
      if (s.State) obj[normalize(s.State)] = s;
    });
    return obj;
  }, [stateData]);

  const districtMap = useMemo(() => {
    const obj = {};
    districtData.forEach(d => {
      if (d.State && d.District) {
        obj[`${normalize(d.State)}__${normalize(d.District)}`] = d;
      }
    });
    return obj;
  }, [districtData]);

  /* ================= COLOR SCALE ================= */

  const getColor = (value) =>
    value > 500000 ? "#4B0B8E" :
    value > 200000 ? "#6D4E94" :
    value > 100000 ? "#8E94B0" :
    value > 50000  ? "#B8C9D5" :
    value > 0      ? "#E3F2FD" :
                     "#f2f2f2";

  /* ================= STYLES ================= */

  const stateStyle = (feature) => ({
    fillColor: getColor(
      stateMap[normalize(feature.properties.st_nm)]?.totalCount || 0
    ),
    weight: 1,
    color: "#fff",
    fillOpacity: 0.85
  });

  const districtStyle = (feature) => ({
    fillColor: getColor(
      districtMap[
        `${normalize(feature.properties.st_nm)}__${normalize(feature.properties.NAME_2)}`
      ]?.totalCount || 0
    ),
    weight: 1,
    color: "#fff",
    fillOpacity: 0.85
  });

  /* ================= POPUPS ================= */

  const onEachState = (feature, layer) => {

    const stateName = feature.properties.st_nm;
    const data = stateMap[normalize(stateName)] || {};

    const {
      maleCount = 0,
      femaleCount = 0,
      othersCount = 0,
      totalCount = 0
    } = data;

    const btnId = `btn-${normalize(stateName)}`;

    const popupContent = `
      <div><b>${stateName.toUpperCase()}</b></div>
      <div>Males: ${maleCount.toLocaleString()}</div>
      <div>Females: ${femaleCount.toLocaleString()}</div>
      <div>Others: ${othersCount.toLocaleString()}</div>
      <div><b>Total: ${totalCount.toLocaleString()}</b></div>
      <br/>
      <button id="${btnId}">Show Districts</button>
    `;

    layer.bindPopup(popupContent);

    layer.on("popupopen", () => {
      document.getElementById(btnId)?.addEventListener("click", () =>
        loadDistricts(stateName)
      );
    });
  };

  const onEachDistrict = (feature, layer) => {

    const districtName = feature.properties.NAME_2;
    const stateName = feature.properties.st_nm;

    const data =
      districtMap[
        `${normalize(stateName)}__${normalize(districtName)}`
      ] || {};

    const {
      maleCount = 0,
      femaleCount = 0,
      othersCount = 0,
      totalCount = 0
    } = data;

    const popupContent = `
      <div><b>${districtName.toUpperCase()}</b></div>
      <div>Males: ${maleCount.toLocaleString()}</div>
      <div>Females: ${femaleCount.toLocaleString()}</div>
      <div>Others: ${othersCount.toLocaleString()}</div>
      <div><b>Total: ${totalCount.toLocaleString()}</b></div>
    `;

    layer.bindPopup(popupContent);
  };

  /* ================= BACK ================= */

  const handleBack = () => {
    setShowDistrictMap(false);
    setClickedState(null); // 🔥 VERY IMPORTANT
    setDistrictGeo(null);
    setDistrictData([]);
  
    // 🔥 Force reload of state data
    fetch("https://api-sales-insights.haqdarshak.com/api/citizen-impact/map-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        gender: selectedGender || [],
        ageRange: selectedAgeRange || [],
        state: selectedState || [],
        district: selectedDistrict || [],
        projectName: selectedProjectName || [],
        startDate: selectedDateRange?.start,
        endDate: selectedDateRange?.end
      })
    })
      .then(res => res.json())
      .then(data => {
        setStateData(Array.isArray(data) ? data : []);
      })
      .catch(() => setStateData([]));
  
    if (mapRef.current && stateGeo) {
      mapRef.current.fitBounds(L.geoJSON(stateGeo).getBounds());
    }
  };

  /* ================= RENDER ================= */

  return (
    <div className="citizen-impact-map">

      <MapContainer
        zoom={5}
        center={[22.9, 82.6]}
        style={{ height: "100%", width: "100%" }}
        whenCreated={(map) => (mapRef.current = map)}
        scrollWheelZoom={false}
      >

        {!showDistrictMap && stateGeo && (
          <GeoJSON
            key={JSON.stringify(stateData)}
            data={stateGeo}
            style={stateStyle}
            onEachFeature={onEachState}
          />
        )}

        {showDistrictMap && districtGeo && (
          <>
            <GeoJSON
              key={JSON.stringify(districtData)}
              data={districtGeo}
              style={districtStyle}
              onEachFeature={onEachDistrict}
            />
            <button className="back-button" onClick={handleBack}>
              Back to State Map
            </button>
          </>
        )}

      </MapContainer>
    </div>
  );
};

export default CitizenImpactMap;